import { useAuthQuery } from '@nhost/react-apollo'
import { GET_ARTICLE_VISIBILITY_DATA } from './api'

export type NumberOperator = '>' | '>=' | '<' | '<='

export type Criterion = { condition: boolean } | { operator: NumberOperator; value: number }

function parseArticleSlugs(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  return raw.filter((s): s is string => typeof s === 'string' && !!s)
}

function criterionMatches(criterion: unknown, answerValue: unknown): boolean {
  if (!criterion || typeof criterion !== 'object') return false
  const c = criterion as Record<string, unknown>

  if (typeof c.operator === 'string' && typeof c.value === 'number') {
    if (typeof answerValue !== 'number') return false
    switch (c.operator as NumberOperator) {
      case '>':
        return answerValue > c.value
      case '>=':
        return answerValue >= c.value
      case '<':
        return answerValue < c.value
      case '<=':
        return answerValue <= c.value
      default:
        return false
    }
  }

  if (typeof c.condition === 'boolean') {
    return answerValue === c.condition
  }

  return false
}

export function useArticleVisibility() {
  const { data, loading } = useAuthQuery(GET_ARTICLE_VISIBILITY_DATA, {
    fetchPolicy: 'cache-and-network',
    // Ohne Zuordnungen bleibt jeder Artikel sichtbar - das ist der harmlose Rückfall,
    // aber es soll nicht unbemerkt passieren.
    onError: (err) => console.error('Failed to load article visibility data:', err),
  })

  const mappingRows = data?.matching_question_with_chapter ?? []
  // Die Antworten werden über den `answer_key` der Zuordnungszeile nachgeschlagen, also
  // dynamisch - deshalb hier als Feldname-zu-Wert-Karte statt über den Zeilentyp.
  const answers: Record<string, unknown> = data?.preparation?.[0] ?? {}

  const matchingRows = mappingRows.filter((row) =>
    criterionMatches(row.criterion, answers[row.answer_key])
  )
  const exclusiveMatchingRows = matchingRows.filter((row) => row.exclusive === true)

  // Artikel, die in mindestens einer Fragebogen-Zuordnung erwähnt werden ("nicht general")
  const gatedSlugs = new Set<string>()
  mappingRows.forEach((row) => {
    parseArticleSlugs(row.article_slugs).forEach((slug) => gatedSlugs.add(slug))
  })

  // Artikel, bei denen mindestens eine der Bedingungen aktuell zutrifft
  const unlockedSlugs = new Set<string>()
  matchingRows.forEach((row) => {
    parseArticleSlugs(row.article_slugs).forEach((slug) => unlockedSlugs.add(slug))
  })

  // Wenn mindestens eine zutreffende Bedingung "exklusiv" ist, werden ausschließlich
  // deren Artikel gezeigt - auch "general"-Artikel werden dann ausgeblendet.
  let exclusiveVisibleSlugs: Set<string> | null = null
  if (exclusiveMatchingRows.length > 0) {
    exclusiveVisibleSlugs = new Set<string>()
    exclusiveMatchingRows.forEach((row) => {
      parseArticleSlugs(row.article_slugs).forEach((slug) => exclusiveVisibleSlugs!.add(slug))
    })
  }

  const isArticleVisible = (slug: string): boolean => {
    if (exclusiveVisibleSlugs) return exclusiveVisibleSlugs.has(slug)
    if (!gatedSlugs.has(slug)) return true
    return unlockedSlugs.has(slug)
  }

  const isChapterVisible = (chapterArticleSlugs: string[]): boolean => {
    if (chapterArticleSlugs.length === 0) return true
    return chapterArticleSlugs.some((slug) => isArticleVisible(slug))
  }

  return { isArticleVisible, isChapterVisible, loading }
}
